<template>
  <div>

    <div v-if="!loading">
      <Step
        v-if="canScrapie === true && Object.keys(scrapieOptions).length > 1"
        class="step"
        :active="isActiveStep('scrapieStep')"
        :can-continue="selectedScrapie !== null"
        :complete="steps.scrapieStep"
        @edit="backTo('scrapieStep')"
        @continue="steps.scrapieStep = true; scrapieBegan = true;"
      >
        <template #label>
          Do you need <a class="text-italic text-bold" href="#" @click.prevent="isScrapieModalOpen = true">Scrapie&nbsp;approved</a> tags?
        </template>

        <OptionToggle
          style="margin-top: .35em;"
          :selected="selectedScrapie"
          @update:selected="setScrapie"
          :items="scrapieOptions"
        />

        <template #display-value>
          <strong v-text="selectedScrapie"></strong>
        </template>
      </Step>

      <Step
        v-if="canEid === true && Object.keys(frequencyOptions).length > 1"
        class="step"
        :active="isActiveStep('frequencyStep')"
        :can-continue="selectedFrequency !== null"
        :complete="steps.frequencyStep"
        @edit="backTo('frequencyStep')"
        @continue="steps.frequencyStep = true;"
      >
        <template #label>
          <div class="flex flex-align-center">
            Choose transponder
            <a
              href="#"
              aria-label="help icon"
              class="icon-info"
              style="text-decoration: none; margin-left: .5em;"
              @click.prevent="isEidModalOpen = true"
            >
              <span class="sr-only">Help icon</span>
            </a>
          </div>
        </template>

        <OptionToggle
          style="margin-top: .35em;"
          :selected="selectedFrequency"
          @update:selected="setFrequency"
          :items="frequencyOptions"
        />

        <template #display-value>
          <strong v-text="selectedFrequency"></strong>
        </template>
      </Step>

      <Step
        class="step"
        :active="isActiveStep('colorStep')"
        :can-continue="Object.keys(selectedVariation).length > 0"
        :complete="steps.colorStep"
        @edit="backTo('colorStep')"
        @continue="steps.colorStep = true"
      >
        <template #label>
          Choose a color
        </template>

        <VariationSelector
          :items="displayVariations"
          :backorder-days="backorderDays"
          :selected="selectedVariation"
          @update:selected="setVariation"
        />

        <template #display-value>
          <div>
            <strong>Color: </strong>
            <span
              v-html="selectedVariation.title || '--'"
              class="text-normal"
            ></span>
          </div>

          <div style="margin-top: .35em;">
            <strong
              v-text="selectedVariation.uom == 'EACH' ? 'Price:' : 'Package Price:'"
            ></strong>
            <span class="text-normal">
              {{ selectedVariation.actualPrice ? formattedPrice(selectedVariation.actualPrice) : '--' }}
              <small class="text-muted" v-if="selectedVariation.uom != 'EACH'">/pkg</small>
            </span>
          </div>
        </template>
      </Step>

      <Step
        class="step"
        :active="isActiveStep('quantityStep')"
        :can-continue="!!quantity"
        :complete="steps.quantityStep"
        @edit="backTo('quantityStep')"
        @continue="steps.quantityStep = true"
      >
        <template #label>
          Set desired tag quantity
        </template>

        <div v-if="selectedVariation.uom != 'EACH'">
          <strong>Package Qty</strong>
          <small
            v-html="`${packageQty(selectedVariation.uom)}/pkg`"
            class="text-muted"
          ></small>
        </div>
        <div class="flex flex-align-center" style="margin-top: .35em;">
          <div
            v-if="selectedVariation.uom == 'EACH'"
            style="margin-right: .5em;"
          >
            <strong>Qty:</strong>
          </div>

          <input
            class="input-field input-autowidth text-center"
            style="width: 4em;"
            type="number"
            min="1"
            step="1"
            value="1"
            @blur="setMinimumNumber($event, 1)"
            v-model.number="quantity">

          <span
            style="padding-left: .75em;"
            v-html="selectedVariation.uom == 'EACH' ? 'tags' : `${quantity * packageQty(selectedVariation.uom)}&nbsp;tags`"
          ></span>
        </div>

        <template #display-value>
          <div>
            <strong>Qty: </strong>
            <span class="text-normal">
              {{ quantity }}
              <span v-if="selectedVariation.uom == 'EACH'">tag strips</span>
              <span v-if="selectedVariation.uom != 'EACH'" v-text="quantity === 1 ? 'pkg' : 'pkgs'" />
              <small
                v-if="selectedVariation.uom != 'EACH'"
                v-html="`(${quantity * packageQty(selectedVariation.uom)}&nbsp;tags)`"
                class="text-muted"
                style="margin-left: .25em;"
              ></small>
            </span>
          </div>

          <div style="margin-top: .35em;">
            <strong>Total Price: </strong>
            <span
              v-html="formattedPrice(selectedVariation.actualPrice * quantity)"
              class="text-normal"
            ></span>
          </div>
        </template>
      </Step>

      <Step
        v-if="canEid && !showValidator"
        class="step"
        :active="isActiveStep('premiseStep')"
        :can-continue="!!flock.premise_id"
        :complete="steps.premiseStep"
        @edit="backTo('premiseStep')"
        @continue="steps.premiseStep = true"
      >
        <template #label>
          Provide your Premise ID
        </template>

        <div class="validator-container">
          <div class="validator-inner">

            <div class="flex flex-align-center flex-justify-between">
              <div class="validator-steps">
                <div
                  class="validator-step"
                  :class="{
                    'active-step': !flock.premise_id,
                    'completed-step': flock.premise_id,
                    'past-step': flock.premise_id,
                  }"
                >
                  <div class="step-num">
                    <span>1</span>
                  </div>
                  <span class="step-label">
                    PIN
                  </span>
                </div>
                <div
                  class="validator-step"
                  :class="{'active-step': flock.premise_id}"
                >
                  <div class="step-num">
                    <svg style="display: block; height: 1em; width: 1em;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M9 22l-10-10.598 2.798-2.859 7.149 7.473 13.144-14.016 2.909 2.806z"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div style="margin-top: 1em;">
              <strong>Premise ID</strong>
            </div>
            <div class="flex flex-direction-column" style="max-width: 16em; margin-top: .35em;">
              <input
                class="input-field"
                type="text"
                pattern="[a-zA-Z0-9]*"
                placeholder="Ex: ABC1234"
                @keydown="alphaNumericOnly"
                v-model="flock.premise_id"
              >
              <div class="text-tiny text-italic" style="font-weight: 400; margin-top: .5em;">
                You must have a PIN to purchase official electronic animal identification tags.
                You can <a href="https://www.aphis.usda.gov/animal-disease/traceability/pin" target="_blank">apply for a Premises ID</a>
                through your state’s animal health department.
              </div>
            </div>
          </div>
        </div>

        <template #display-value>
          <strong v-text="flock.premise_id"></strong>
        </template>
      </Step>

      <Step
        v-if="showValidator"
        class="step"
        :active="isActiveStep('validateStep')"
        :can-continue="Object.keys($refs).length > 0 && $refs['flock-validator'] && $refs['flock-validator'].confirmed"
        :complete="steps.validateStep"
        @edit="backTo('validateStep')"
        @continue="steps.validateStep = true"
      >
        <template #label>
          Validate your flock information
        </template>

        <FlockValidator
          ref="flock-validator"
          :flock="flock"
          :checkPremise="canEid"
          @store-sequences="pastSequences = $event"
          @update="updateFlock"
        />

        <template #display-value>
          <div>
            <strong>Flock ID: </strong>
            <span
              v-text="flock.id"
              class="text-normal"
            ></span>
          </div>

          <div v-if="canEid">
            <strong>Premise ID: </strong>
            <span
              v-text="flock.premise_id"
              class="text-normal"
            ></span>
          </div>

          <div class="flex flex-align-start" style="margin-top: .35em;">
            <div>
              <strong>Address: </strong>
            </div>
            <div class="text-normal leading-tight" style="margin-left: .35em;">
              {{ flock.address.first_name }} {{ flock.address.last_name }}<br>
              {{ flock.address.street }}<br>
              {{ flock.address.city }}, {{ flock.address.state }} {{ flock.address.zip }}
            </div>
          </div>
        </template>
      </Step>


      <Step
        class="step"
        :active="isActiveStep('designStep')"
        :can-continue="hasTemplates &&stepDesignCanContinue"
        :complete="steps.designStep"
        continue-text="Save &amp; Review"
        ref="design-step"
        @edit="backTo('designStep')"
        @continue="saveAndReview"
      >
        <template #label>
          Design your tags
        </template>

        <div v-if="!hasTemplates">
          <div class="alert alert-warning">There are no customizable templates available for this ear tag.</div>
        </div>
        <template v-else>
          <Tabs
            :key="tabsRerender"
          >
            <template slot-scope="{ switchTab }">
              <Tab
                title="Female Side"
                :has-warning="!($refs.builder1 || {}).isComplete || ($refs.builder3 && !($refs.builder3 || {}).isComplete)"
                warning-class="text-danger"
                ref="female-tab"
                v-if="showFemaleTab"
              >
                <div
                  :class="{'flex flex-wrap': selectedVariation.panels === 4}"
                  :style="[selectedVariation.panels === 4 ? {gap: '2em', } : {}]"
                >
                  <div
                    :class="{'flex-grow': selectedVariation.panels === 4}"
                    :style="[selectedVariation.panels === 4 ? {'flex-basis': '225px', } : {}]"
                  >
                    <TemplateBuilder
                      ref="builder1"
                      style="margin-bottom: 1em;"
                      :single-column="selectedVariation.panels === 4"
                      :image="femaleSideImage"
                      :lines.sync="filledLines.female.lines"
                      :loading="fetchingTemplates"
                      :logo="uploadedLogo"
                      :quantity="totalQuantity"
                      :scrapie="selectedScrapie === 'Yes'"
                      :eid="canEid"
                      :sequences="pastSequences"
                      :selected-template.sync="filledLines.female.template"
                      :sequence="filledLines.female.sequence"
                      :templates="femaleTemplates"
                      @builder-done="builder1Done = $event"
                      @rendered="debouncedGeneratePreview"
                      @update:sequence="setSequence($event, 'female')"
                      @uploaded-logo="updateLogo"
                    />

                    <label
                      class="wrapping-label"
                      :class="{'disabled': disableMirrorEdit}"
                      style="cursor: pointer"
                      v-if="selectedVariation.panels === 4"
                    >
                      <input
                        v-if="!disableMirrorEdit"
                        type="checkbox"
                        :true-value="true"
                        :false-value="false"
                        v-model="filledLines.female.mirror"
                      >
                      <div class="fake-check" v-else>
                        <span>&check;</span>
                      </div>
                      <span class="label-contents" style="padding-left: 2em; padding-top: .5em; padding-bottom: .5em;">
                        Mirror Visual Mate
                      </span>
                      <svg v-if="disableMirrorEdit" class="block text-muted" style="position: absolute; right: .5em; top: 0; height: 2.25em;" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                        <use href="#input-lock-icon"></use>
                      </svg>
                    </label>
                  </div>
                  <div
                    v-if="selectedVariation.panels === 4"
                    class="flex-grow"
                    style="flex-basis: 225px;"
                  >
                    <TemplateBuilder
                      style="margin-bottom: 1em;"
                      ref="builder3"
                      :single-column="true"
                      :disabled="!!selectedFemaleTemplate.mirror_template"
                      :image="femaleVisualMateImage"
                      :lines.sync="filledLines.female_vm.lines"
                      :loading="fetchingTemplates"
                      :logo="uploadedLogo"
                      :quantity="totalQuantity"
                      :scrapie="selectedScrapie === 'Yes'"
                      :eid="canEid"
                      :sequences="pastSequences"
                      :selected-template.sync="filledLines.female_vm.template"
                      :sequence="filledLines.female_vm.sequence"
                      :templates="vmFemaleTemplates"
                      @builder-done="builder3Done = $event"
                      @rendered="debouncedGeneratePreview"
                      @update:sequence="setSequence($event, 'female')"
                      @uploaded-logo="updateLogo"
                    />

                  </div>
                </div>

                <div class="text-right" style="padding-top: 1em;">
                  <button type="button"
                    class="button button-smaller button-link button-arrowed"
                    style="padding: 0;"
                    @click="switchTab(1)"
                    v-if="Object.keys(maleTemplates).length > 0"
                  >
                    Customize Male Side
                  </button>
                </div>
              </Tab>
              <Tab
                title="Male Side"
                :has-warning="!($refs.builder2 || {}).isComplete || ($refs.builder4 && !($refs.builder4 || {}).isComplete)"
                warning-class="text-danger"
                ref="male-tab"
                v-if="showMaleTab"
              >
                <div
                  :class="{'flex flex-wrap': selectedVariation.panels === 4}"
                  :style="[selectedVariation.panels === 4 ? {gap: '2em'} : {}]"
                >
                  <div
                    :class="{'flex-grow': selectedVariation.panels === 4}"
                    :style="[selectedVariation.panels === 4 ? {'flex-basis': '225px', } : {}]"
                  >
                    <TemplateBuilder
                      ref="builder2"
                      style="margin-bottom: 1em;"
                      :single-column="selectedVariation.panels === 4"
                      :disabled="!!selectedFemaleTemplate.mate_mirror_template"
                      :image="maleSideImage"
                      :lines.sync="filledLines.male.lines"
                      :loading="fetchingTemplates"
                      :logo="uploadedLogo"
                      :quantity="totalQuantity"
                      :scrapie="selectedScrapie === 'Yes'"
                      :eid="canEid"
                      :sequences="pastSequences"
                      :selected-template.sync="filledLines.male.template"
                      :sequence="filledLines.male.sequence"
                      :templates="maleTemplates"
                      @builder-done="builder2Done = $event"
                      @rendered="debouncedGeneratePreview"
                      @update:sequence="setSequence($event, 'male')"
                      @uploaded-logo="updateLogo"
                    />

                    <label
                      class="wrapping-label"
                      :class="{'disabled': disableMirrorEdit}"
                      style="cursor: pointer"
                      v-if="selectedVariation.panels === 4"
                    >
                      <input
                        v-if="!disableMirrorEdit"
                        type="checkbox"
                        :true-value="true"
                        :false-value="false"
                        v-model="filledLines.male.mirror"
                      >
                      <div class="fake-check" v-else>
                        <span>&check;</span>
                      </div>
                      <span class="label-contents" style="padding-left: 2em; padding-top: .5em; padding-bottom: .5em;">
                        Mirror Visual Mate
                      </span>
                      <svg v-if="disableMirrorEdit" class="block text-muted" style="position: absolute; right: .5em; top: 0; height: 2.25em;" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                        <use href="#input-lock-icon"></use>
                      </svg>
                    </label>
                  </div>
                  <div
                    v-if="selectedVariation.panels === 4"
                    class="flex-grow"
                    style="flex-basis: 225px;"
                  >
                    <TemplateBuilder
                      style="margin-bottom: 1em;"
                      ref="builder4"
                      :single-column="true"
                      :disabled="!!selectedMaleTemplate.mirror_template"
                      :image="maleVisualMateImage"
                      :lines.sync="filledLines.male_vm.lines"
                      :loading="fetchingTemplates"
                      :logo="uploadedLogo"
                      :quantity="totalQuantity"
                      :scrapie="selectedScrapie === 'Yes'"
                      :eid="canEid"
                      :sequences="pastSequences"
                      :selected-template.sync="filledLines.male_vm.template"
                      :sequence="filledLines.male_vm.sequence"
                      :templates="vmMaleTemplates"
                      @builder-done="builder4Done = $event"
                      @rendered="debouncedGeneratePreview"
                      @update:sequence="setSequence($event, 'male')"
                      @uploaded-logo="updateLogo"
                    />

                  </div>
                </div>
                <div class="text-left" style="padding-top: 1em;">
                  <button type="button"
                    class="button button-smaller button-link button-arrowed-left"
                    style="padding: 0;"
                    @click="switchTab(0)"
                    v-if="Object.keys(femaleTemplates).length > 0"
                  >
                    Customize Female Side
                  </button>
                </div>
              </Tab>
            </template>
          </Tabs>

          <div v-if="!canEid && pastSequences.length > 0" style="margin-top: .5em;">
            <button
              type="button"
              class="expand-tab"
              @click="showPastSequences = !showPastSequences">
              Review past sequences
              <span v-html="showPastSequences ? '[-]' : '[+]'"></span>
            </button>

            <div :class="{'hidden': !showPastSequences}">
              <div class="expandable-scroll text-smaller">
                  <div
                    class="sequence-row flex flex-justify-between"
                    v-for="(sequence, i) in pastSequences" :key="`sequence-${i}`">
                    <span v-html="`${sequence.startAin} &ndash; ${sequence.endAin}`"></span>
                    <span
                      v-html="formatDate(sequence.eventDate)"
                      style="white-space: nowrap;"
                    ></span>
                  </div>
              </div>
            </div>
          </div>

          <div style="margin-top: .5em;">
            <label class="wrapping-label">
              <input type="checkbox" v-model="confirmPreview">
              <span class="label-contents" style="padding-left: 2em;">
                I understand this is only a preview of printed tags, and the final product may vary. We will make every attempt to make the letters and numbers as large as&nbsp;possible.
              </span>
            </label>
          </div>

          <div v-if="Object.keys($refs).length">
            <div v-show="confirmPreview && (($refs['female-tab'] && $refs['female-tab'].hasWarning) || ($refs['male-tab'] && $refs['male-tab'].hasWarning))">
              <div class="text-red text-tiny">
                You must complete all selected text fields for both the Female and Male sides.
              </div>
            </div>
          </div>
        </template>

        <template #display-value>
          <img
            class="img-responsive"
            alt="Tags preview"
            :src="renderedPreview"
          >
        </template>

        <template #after-action>
          <div class="text-smaller" style="margin-top: 1.5em;">
            Need a different customization option? You can provide special instructions during checkout. Or call us at <a class="text-bold" href="tel:18002826631">800-282-6631</a> to place your order.
          </div>
        </template>
      </Step>

      <!-- <img :src="renderedPreview" alt=""> -->

      <!-- <div class="form-row row" style="margin-top: 1em;">
        <div class="form-column col-xs-12 col-md-8">
          <label class="label" for="ear-tag-note">
            Special Instructions
            <span class="text-muted text-tiny">(Optional)</span>
          </label>
          <textarea
            class="input-field"
            id="ear-tag-note"
            v-model="note"
            cols="40"
            rows="3"
          ></textarea>
        </div>
      </div> -->

      <div style="margin-top: 1.5em;">
        <slot
          name="actions"
          v-bind="{completed: completed, data: saveProperties, preview: renderedPreview}"
        />
      </div>
    </div>
    <div v-else>
      <Step
        class="step"
        :force="true"
        v-for="n in 3"
        :key="`fake-${n}`"
      >
        <template #label>
          <div class="text-muted flex flex-align-center">
            <span class="flex flex-align-center" v-if="n === 1">
              <span>Loading</span>
              <div
                class="loading-spinner is-spinning"
                style="width: 18px; height: 18px; border-width: 3px; border-color: inherit; border-right-color: transparent; margin-left: 10px;"></div>
            </span>
            <svg v-else style="color: #eaeaea;" class="block" width="120" height="15" fill="currentColor">
              <rect width="100%" height="15"></rect>
            </svg>
          </div>
        </template>

        <template #display-value>
          <div style="color: #eaeaea;">
            <svg style="display: block;" width="200" height="15" fill="currentColor">
              <rect width="100%" height="15"/>
            </svg>
            <svg style="display: block; margin-top: .35em;" width="160" height="15" fill="currentColor">
              <rect width="100%" height="15"/>
            </svg>
          </div>
        </template>
      </Step>
    </div>

    <MountingPortal
      mountTo="#assistive-portal"
      append
    >
      <Modal :open="isScrapieModalOpen" @close="isScrapieModalOpen = false">
        <h2 class="like-h3 text-dark-blue">
          Scrapie Approved
        </h2>

        <p>
          The National Scrapie Eradication Program (NSEP) coordinated by the USDA is a joint effort that includes participation by state governments and industry, particularly producers. To comply, producers must insert an official tag in an ear of show sheep and goats, sheep and goats over 18 months of age, and intact sheep and goats under 18 months of age not moving into slaughter channels prior to leaving their farm of origin/birth.<br>
          <br>
          Visit <a href="http://eradicatescrapie.org/" target="_blank">eradicatescrapie.org</a> for more details.
        </p>
        <div class="text-right" style="border-top: 1px solid #e5e5e5; margin: 0 -1em; padding: 1em 1em 0;">
          <button type="button" class="button" @click="isScrapieModalOpen = false">
            Close
          </button>
        </div>
      </Modal>

      <Modal :open="isEidModalOpen" @close="isEidModalOpen = false">
        <h2 class="like-h3 text-dark-blue">
          About Transponders
        </h2>

        <p>
          HDX and FDX tags both adhere to ISO standards and are compatible with the same EID readers.
          HDX tags excel at transmitting signals through interference, such as metal objects and fluorescent lights.
          FDX tags are suitable when read range is less critical. Typical read ranges are 15&ndash;18" for HDX tags
          and 13&ndash;16" for FDX tags.
        </p>
        <div class="text-right" style="border-top: 1px solid #e5e5e5; margin: 0 -1em; padding: 1em 1em 0;">
          <button type="button" class="button" @click="isEidModalOpen = false">
            Close
          </button>
        </div>
      </Modal>
    </MountingPortal>

  </div>
</template>

<script>
import { debounce, throttle, formatDate, formattedPrice, packageQty, setMinimumNumber, EartagPreview } from './helpers.js';

import EarTagItem from './components/EarTagItem';
import FlockValidator from './components/FlockValidator';
import Modal from './components/Modal';
import OptionToggle from './components/OptionToggle';
import Step from './components/Step';
import Tab from './components/Tab';
import Tabs from './components/Tabs';
import TemplateBuilder from './components/TemplateBuilder';
import VariationSelector from './components/VariationSelector';

const BOTH_SIDE = 1;
const FEMALE_SIDE = 2;
const MALE_SIDE = 3;

const AVAILABLE_MAIN_MATE = 1;
const AVAILABLE_MAIN = 2;
const AVAILABLE_MATE = 3;

const templateSide = () => ({
  lines: [],
  mirror: true,
  sequence: '',
  template: '',
});

const templateMateSide = () => ({
  lines: [],
  sequence: '',
  template: '',
});

const defaultRenderLayout = () => ({
  female: [0, 0, 0],
  male: [0, 1, 0],
});

const previewImageWidth = 300;
const previewImageHeight = 300;

const srcImageWidth = 400;
const srcImageHeight = 400;

const initialState = () => ({
  scrapieBegan: false,
  builder1Done: false,
  builder2Done: false,
  builder3Done: false,
  builder4Done: false,
  confirmPreview: false,
  fetchingTemplates: true,
  filledLines: {
    female: templateSide(),
    female_vm: templateMateSide(),
    male: templateSide(),
    male_vm: templateMateSide(),
  },
  flock: {
    id: '',
    premise_id: '',
    address: {
      first_name: '',
      last_name: '',
      street: '',
      city: '',
      state: '',
      zip: '',
    },
    confirm: false,
  },
  isEidModalOpen: false,
  isScrapieModalOpen: false,
  // note: '',
  loading: true,
  localVariations: [],
  pastSequences: [],
  quantity: 1,
  renderLayout: defaultRenderLayout(),
  renderedPreview: '',
  tabsRerender: Math.random(),
  selectedScrapie: '',
  selectedVariation: {},
  selectedFrequency: '',
  showPastSequences: false,
  showTemplates: false,
  steps: {
    scrapieStep: false,
    frequencyStep: false,
    colorStep: false,
    quantityStep: false,
    premiseStep: false,
    validateStep: false,
    designStep: false,
  },
  templates: {},
  uploadedLogo: {
    origname: '',
    path: '',
  },
});

export default {
  name: 'app',

  components: {
    EarTagItem,
    FlockValidator,
    Modal,
    OptionToggle,
    Step,
    Tab,
    Tabs,
    TemplateBuilder,
    VariationSelector,
  },

  props: {
    backorderDays: {
      default: 10,
      type: Number,
    },
    disableMirrorEdit: {
      default: true,
      type: Boolean,
    },
    prodId: {
      default: null,
      type: Number,
    },
    initialData: {
      default: () => {},
      type: Object,
    },
    variations: {
      default: () => [],
      type: Array,
    },
  },

  data: () => initialState(),

  async created() {
    if(!this.prodId && this.variations.length === 0) {
      // cannot run
    }

    this.debouncedGeneratePreview = debounce(this.generatePreview, 500);

    // fetch variations
    if(!this.variations || this.variations.length === 0) {
      await this.loadVariations();
    } else {
      this.localVariations = this.variations;
    }

    this.selectedVariation = this.localVariations[0];

    if(Object.keys(this.initialData || {}).length > 0) {
      await this.initFromSavedData();
    } else {
      this.selectedScrapie = Object.keys(this.scrapieOptions)[0];
      this.fastForwardScrapie();

      this.setFrequency(Object.keys(this.frequencyOptions)[0]);
      this.fastForwardFrequency();

      this.templates = Object.assign({}, await this.loadTemplates());
    }

    this.$nextTick(() => {
      this.loading = false;
    });
  },

  methods: {
    alphaNumericOnly(e) {
      if(!e.key.match(/^[a-zA-Z0-9]*$/)) {
        e.preventDefault();
        return false;
      }
    },

    backTo(step) {
      this.$root.alertVisible = false;

      const steps = Object.keys(this.steps);

      this.$set(this.steps, step, false);

      for(let i = steps.indexOf(step); i < steps.length; i++) {
        if(steps[i] === 'frequencyStep') {
          this.fastForwardFrequency();
        } else if(steps[i] === 'scrapieStep') {
          this.fastForwardScrapie();
        } else {
          this.steps[steps[i]] = false;
        }
      }

      this.confirmPreview = false;
    },

    formatDate,

    formattedPrice,

    flipPreviewContext(ctx, side) {
      ctx.translate(
        ((previewImageWidth * side[1]) + (previewImageWidth / 2)),
        ((previewImageHeight * side[0]) + (previewImageHeight / 2))
      );
      ctx.rotate(Math.PI);
      ctx.translate(
        -((previewImageWidth * side[1]) + (previewImageWidth / 2)),
        -((previewImageHeight * side[0]) + (previewImageHeight / 2))
      );
    },

    generatePreview(e) {
      const builders = {
        female: 'builder1',
        male: 'builder2',
        female_vm: 'builder3',
        male_vm: 'builder4',
      };

      const layout = this.renderLayout;
      const femaleTemplate = this.filledLines.female.template
        ? Object.values(this.templates).find(t => t.label === this.filledLines.female.template)
        : null;
      const maleTemplate = this.filledLines.male.template
        ? Object.values(this.templates).find(t => t.label === this.filledLines.male.template)
        : null;
      const femaleMateTemplate = this.filledLines.female_vm.template
        ? Object.values(this.templates).find(t => t.label === this.filledLines.female_vm.template)
        : null;
      const maleMateTemplate = this.filledLines.male_vm.template
        ? Object.values(this.templates).find(t => t.label === this.filledLines.male_vm.template)
        : null;

      let canvasWidth = 0;
      let canvasHeight = 0;
      let canvasRows = Object.values(layout).reduce((obj, side) => {
        if(obj.hasOwnProperty(side[0]) === false) {
          obj[side[0]] = {};
        }

        return obj;
      }, {});

      if(this.$refs.builder1 && this.$refs.builder1.$refs && this.$refs.builder1.$refs.artboard && layout.hasOwnProperty('female')) {
        canvasRows[layout.female[0]][layout.female[1]] = new EartagPreview('female', femaleTemplate);

        // if((layout.female[2] || 0) === 1) {
        //   this.flipPreviewContext(ctx, layout.female);
        // }
      }

      if(this.$refs.builder2 && this.$refs.builder2.$refs && this.$refs.builder2.$refs.artboard && layout.hasOwnProperty('male')) {
        canvasRows[layout.male[0]][layout.male[1]] = new EartagPreview('male', maleTemplate);

        // if((layout.male[2] || 0) === 1) {
        //   this.flipPreviewContext(ctx, layout.male);
        // }
      }

      if(this.$refs.builder3 && this.$refs.builder3.$refs && this.$refs.builder3.$refs.artboard && layout.hasOwnProperty('female_vm')) {
        canvasRows[layout.female_vm[0]][layout.female_vm[1]] = new EartagPreview('female_vm', femaleMateTemplate);

        // if((layout.female_vm[2] || 0) === 1) {
        //   this.flipPreviewContext(ctx, layout.female_vm);
        // }
      }

      if(this.$refs.builder4 && this.$refs.builder4.$refs && this.$refs.builder4.$refs.artboard && layout.hasOwnProperty('male_vm')) {
        canvasRows[layout.male_vm[0]][layout.male_vm[1]] = new EartagPreview('male_vm', maleMateTemplate);

        // if((layout.male_vm[2] || 0) === 1) {
        //   this.flipPreviewContext(ctx, layout.male_vm);
        // }
      }


      // determine max canvas size
      canvasWidth = Math.max(...Object.values(canvasRows).map(row => Object.values(row).reduce((c, { writeWidth }) => c + writeWidth, 0)));
      canvasHeight = Object.values(canvasRows).reduce((c, row) => c + Math.max(...Object.values(row).map(row => row.writeHeight)), 0);

      // create canvas
      const canvas = document.createElement("canvas");
        canvas.setAttribute('height', canvasHeight);
        canvas.setAttribute('width', canvasWidth);

      let ctx = canvas.getContext('2d', { willReadFrequently: true });

      Object.values(canvasRows).forEach((row, row_i, rows) => {
        Object.values(row).forEach((preview, col_i, previews) => {
          const startX = previews.slice(0, col_i).reduce((c, p) => c + p.writeWidth, 0);
          const startY = rows.slice(0, row_i).reduce((c, r) => c + Math.max(...Object.values(r).map(r => r.writeHeight)), 0);

          // drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, destinationX, destinationY, destinationWidth, destinationHeight)
          ctx.drawImage(
            this.$refs[builders[preview.side]].$refs.artboard.$refs.canvas,
            preview.readX,
            preview.readY,
            preview.srcDrawWidth,
            preview.srcDrawHeight,
            startX,
            startY,
            preview.writeWidth,
            preview.writeHeight
          );
        });
      });

      const image = canvas.toDataURL();

      this.renderedPreview = image;
    },

    async initFromSavedData() {
      let i = 0;

      Object.assign(this.$data, initialState(), {
        confirmPreview: true,
        flock: (this.initialData.properties || {}).flock || initialState().flock,
        localVariations: this.localVariations,
        // note: (this.initialData.properties || {}).note || '',
        quantity: this.initialData.quantity,
        selectedFrequency: ((this.initialData.properties || {}).eid || false) ? ((this.initialData.properties || {}).frequency || Object.keys(this.frequencyOptions)[0]) : '',
        selectedScrapie: ((this.initialData.properties || {}).scrapie !== undefined) ? ((this.initialData.properties || {}).scrapie ? 'Yes' : 'No') : Object.keys(this.scrapieOptions)[0],
        selectedVariation: this.localVariations.find(v => v.sku === this.initialData.sku) || this.localVariations[0],
        steps: Object.keys(initialState().steps).reduce((carry, step) => {
          carry[step] = true;

          return carry;
        }, {}),
        uploadedLogo: Object.assign(initialState().uploadedLogo, (this.initialData.properties || {}).logo || {}),
      });

      this.fastForwardScrapie();
      this.fastForwardFrequency();

      this.templates = Object.assign({}, await this.loadTemplates());

      let sideProps, side, toFillLines = {};
      for(side in (this.initialData.properties || {}).lines || {}) {
        if(!this.initialData.properties.lines[side]) continue;

        sideProps = this.initialData.properties.lines[side];

        let foundTemplate = Object.values(this.templates).find(template => {
          return template.label == sideProps.template
            && (
              (['male', 'male_vm'].includes(side) && template.side === MALE_SIDE)
              || (['female', 'female_vm'].includes(side) && template.side === FEMALE_SIDE)
              || template.side === BOTH_SIDE
            );
        });

        toFillLines[side] = templateSide();

        if(sideProps.hasOwnProperty('mirror')) {
          toFillLines[side].mirror = sideProps.mirror;
        } else {
          delete toFillLines[side].mirror;
        }

        if(!foundTemplate) {
          toFillLines[side].lines = [];

          continue;
        }

        toFillLines[side]['template'] = sideProps.template;

        toFillLines[side].lines = foundTemplate.lines.slice().map((l, i) => {
          l = Object.assign({}, l);

          l.value = sideProps.lines[i];

          return l;
        });
      }

      this.filledLines = toFillLines;
    },

    isActiveStep(step) {
      const steps = Object.keys(this.steps).filter(s => {
        return !['validateStep', 'scrapieStep', 'frequencyStep', 'premiseStep'].includes(s)
          || (this.canScrapie && this.selectedScrapie === 'Yes' && s === 'validateStep')
          || (this.canScrapie && s === 'scrapieStep')
          || (this.canEid && (!this.canScrapie || this.selectedScrapie !== 'Yes') && s === 'premiseStep')
          || (this.canEid && s === 'frequencyStep');
      }).reverse();

      const index = steps.findIndex(s => s === step);
      const next = steps.slice(index).filter(s => this.steps[s] === false);

      return next.length === 1 && this.steps[step] !== true;
    },

    async loadTemplates() {
      this.fetchingTemplates = true;

      this.templates = Object.assign({});

      const { data: {templates, layout} } = await this.$http.get('/eartag/templates', {
        params: {
          prod_id: this.prodId,
          eid: Number(this.canEid),
          frequency: this.selectedFrequency,
          scrapie: Number(this.selectedScrapie === 'Yes'),
        },
      });

      this.fetchingTemplates = false;

      this.renderLayout = layout || defaultRenderLayout();

      return (typeof templates === Array) ? {} : templates;
    },

    async loadVariations() {
      return await this.$http.get('/eartag/variations', {
        params: {
          id: this.prodId,
        },
      })
      .then(response => this.localVariations = response.data)
      .catch(error => {
        // if((error.response || {}).status === 404) {
          //
        // }
      });
    },

    packageQty,

    async resetState(obj = {}) {
      const variations = structuredClone(this.localVariations);

      Object.assign(this.$data, initialState(), {
        fetchingTemplates: false,
        loading: false,
        localVariations: variations,
        selectedFrequency: this.canEid ? Object.keys(this.frequencyOptions)[0] : '',
        selectedScrapie: this.canScrapie ? Object.keys(this.scrapieOptions)[0] : '',
        selectedVariation: Object.assign({}, variations[0]),
      }, obj);

      this.templates = Object.assign({}, await this.loadTemplates());

      this.$nextTick(() => {
        this.fastForwardScrapie();
        this.fastForwardFrequency();
      });
    },

    saveAndReview() {
      this.steps.designStep = true;

      window.scrollTo(0, this.$refs['design-step'].$el.offsetTop);
    },

    setFrequency(value, e) {
      if(value === this.selectedFrequency) {
        return;
      }

      this.tabsRerender = Math.random();

      Object.assign(this.$data, {
        selectedFrequency: value,
      });

      this.selectedVariation = this.displayVariations[0];
    },

    async setScrapie(value, e) {
      if(value === this.selectedScrapie) {
        return;
      }

      this.tabsRerender = Math.random();

      if(!this.scrapieBegan) {
        Object.assign(this.$data, {
          selectedScrapie: value,
        });

        this.templates = Object.assign({}, await this.loadTemplates());
        return;
      }

      if(window.confirm('Changing your selection will reset your progress.')) {
        this.resetState({selectedScrapie: value});

        return;
      }

      e.target.checked = false;
    },

    setSequence(sequence, side) {
      const otherSide = side === 'female' ? 'male' : 'female';

      this.$set(this.filledLines[side], 'sequence', sequence);

      const sideIndex = this.filledLines[side].lines
                          .findIndex(l => (l || {}).type === 'sequence');

      if(sideIndex !== -1) {
        this.$set(this.filledLines[side].lines[sideIndex], 'value', sequence);
      }

      if(this.selectedVariation.panels === 4 && (this.filledLines[side].mirror || this.selectedScrapie === 'Yes')) {
        this.$set(this.filledLines[`${side}_vm`], 'sequence', sequence);

        const sideIndex = this.filledLines[`${side}_vm`].lines
                          .findIndex(l => (l || {}).type === 'sequence');

        if(sideIndex !== -1) {
          this.$set(this.filledLines[`${side}_vm`].lines[sideIndex], 'value', sequence);
        }
      }

      if(this.selectedScrapie === 'Yes') {
        this.$set(this.filledLines[otherSide], 'sequence', sequence);

        const otherSideIndex = this.filledLines[otherSide].lines
                                .findIndex(l => (l || {}).type === 'sequence');

        if(otherSideIndex !== -1) {
          this.$set(this.filledLines[otherSide].lines[otherSideIndex], 'value', sequence);
        }
      }

      if(this.selectedVariation.panels === 4 && (this.filledLines[otherSide].mirror || this.selectedScrapie === 'Yes')) {
        this.$set(this.filledLines[`${otherSide}_vm`], 'sequence', sequence);

        const otherSideMateIndex = this.filledLines[`${otherSide}_vm`].lines
                            .findIndex(l => (l || {}).type === 'sequence');

        if(otherSideMateIndex !== -1) {
          this.$set(this.filledLines[`${otherSide}_vm`].lines[otherSideMateIndex], 'value', sequence);
        }
      }
    },

    setMinimumNumber,

    setVariation(item) {
      this.selectedVariation = Object.assign({}, item);
    },

    updateFlock(flock) {
      flock.id = flock.id.toUpperCase().replace(/[^A-Z0-9]+/i, '');

      this.flock = Object.assign({}, this.flock, flock);
    },

    updateLogo(logo) {
      this.uploadedLogo = Object.assign(initialState().uploadedLogo, logo || {});
    },

    fastForwardFrequency() {
      if(this.canEid && Object.keys(this.frequencyOptions).length === 1) {
        this.steps.frequencyStep = true;
      }
    },

    fastForwardScrapie() {
      if(this.canScrapie && Object.keys(this.scrapieOptions).length === 1) {
        this.steps.scrapieStep = true;
        this.scrapieBegan = true;
      }
    },

    updateVisualMateWith(obj, mateSide) {
      const mirrorTemplate = this.getMirrorTemplate(obj.template)

      if(mirrorTemplate) {
        const newSide = templateMateSide();

        newSide.template = mirrorTemplate.label;
        newSide.lines = mirrorTemplate.lines.slice(0).map((line, i) => {
          if(obj.lines[i] && obj.lines[i].hasOwnProperty('value')) {
            line.value = obj.lines[i].value;
          }

          return Object.assign({}, line);
        });

        this.filledLines[`${mateSide}_vm`] = newSide;
      }
    },

    getMirrorTemplate(template) {
      const masterTemplate = Object.values(this.templates).find(t => t.label === template);

      if(!masterTemplate || !masterTemplate.mirror_template) {
        return;
      }

      const mirrorTemplate = Object.values(this.templates).find(t => t.label === masterTemplate.mirror_template);

      return Object.assign({}, {
        label: mirrorTemplate.label,
        lines: mirrorTemplate.lines,
      });
    },

    getMateMirrorTemplate(template) {
      const masterTemplate = Object.values(this.templates).find(t => t.label === template);

      if(!masterTemplate || !masterTemplate.mate_mirror_template) {
        return;
      }

      const mirrorTemplate = Object.values(this.templates).find(t => t.label === masterTemplate.mate_mirror_template);

      return Object.assign({}, {
        label: mirrorTemplate.label,
        lines: mirrorTemplate.lines,
      });
    },

    updateMateWith(obj, mateSide) {
      const mateMirrorTemplate = this.getMateMirrorTemplate(obj.template);

      if(!mateMirrorTemplate) {
        return;
      }

      const newSide = templateSide();

      newSide.template = mateMirrorTemplate.label;
      newSide.lines = mateMirrorTemplate.lines.slice(0).map((line, i) => {
        if(obj.lines[i] && obj.lines[i].hasOwnProperty('value')) {
          line.value = obj.lines[i].value;
        }

        return Object.assign({}, line);
      });

      this.filledLines[mateSide] = newSide;
    },
  },

  computed: {
    selectedFemaleTemplate() {
      return Object.values(this.templates)
        .find(t => t.label === this.filledLines.female.template) || templateSide();
    },

    selectedMaleTemplate() {
      return Object.values(this.templates)
        .find(t => t.label === this.filledLines.male.template) || templateSide();
    },

    canEid() {
      return !!this.localVariations.find(variation => variation.eid === true);
    },

    canScrapie() {
      return !!this.localVariations.find(variation => variation.scrapie.includes('Yes'));
    },

    completed() {
      let steps = Object.assign({}, this.steps);

      if(!this.canEid) {
        delete steps['frequencyStep'];
      }

      if(!this.canEid || this.showValidator) {
        delete steps['premiseStep'];
      }

      if(!this.showValidator) {
        delete steps['validateStep'];
      }

      if(!this.canScrapie) {
        delete steps['scrapieStep'];
      }

      return Object.keys(steps)
        .filter(s => steps[s] === false)
        .length === 0;
    },

    femaleSideImage() {
      if(Object.keys(this.selectedVariation).length === 0) {
        return '';
      }

      return this.$root.imageDomain
              + this.selectedVariation.sku
              + '-F' + (this.selectedScrapie === 'Yes' ? 'S' : '')
              + '.jpg';
    },

    femaleVisualMateImage() {
      if(Object.keys(this.selectedVariation).length === 0) {
        return '';
      }

      return this.$root.imageDomain
              + this.selectedVariation.sku
              + '-VF' + (this.selectedScrapie === 'Yes' ? 'S' : '')
              + '.jpg';
    },

    femaleTemplates() {
      let baseKeys = Object.keys(this.templates);
      let keys = baseKeys;

      if(this.selectedScrapie === 'Yes') {
        keys = baseKeys.filter(key => this.templates[key].label === 'scrapie_female');
      }

      if(this.selectedScrapie !== 'Yes' || !keys.length) {
        keys = baseKeys.filter(key => {
          if(
            (this.selectedScrapie === 'Yes' && !this.templates[key].scrapie)
            || (this.selectedScrapie !== 'Yes' && this.templates[key].scrapie)
            || (this.canEid && !this.templates[key].eid)
            || (!this.canEid && this.templates[key].eid)
          ) {
            return false;
          }

          return [BOTH_SIDE, FEMALE_SIDE].indexOf(this.templates[key].side) > -1
            && [AVAILABLE_MAIN_MATE, AVAILABLE_MAIN].indexOf(this.templates[key].mate_availability) > -1;
        });
      }

      return keys
        .reduce((carry, key) => {
          carry[key] = this.templates[key];

          return carry;
        }, {});
    },

    vmFemaleTemplates() {
      return Object.keys(this.templates)
        .filter(key => [BOTH_SIDE, FEMALE_SIDE].indexOf(this.templates[key].side) > -1)
        .filter(key => [AVAILABLE_MAIN_MATE, AVAILABLE_MATE].indexOf(this.templates[key].mate_availability) > -1)
        .reduce((carry, key) => {
          carry[key] = this.templates[key];

          return carry;
        }, {});
    },

    displayVariations() {
        if(!this.filteredVariations.length) {
          return [];
        }

        return this.filteredVariations.filter(variation => {
          if(this.selectedFrequency && variation.frequency && variation.frequency.includes(this.selectedFrequency) === false) {
            return false;
          }

          return true;
        });
    },

    filteredVariations() {
      if(!this.localVariations.length) {
        return [];
      }

      return this.localVariations.filter((variation) => {
        if(this.canEid === true && variation.eid !== true) {
          return false;
        }

        if(
          this.canScrapie === true && (
            (this.selectedScrapie === 'Yes' && !variation.scrapie.includes('Yes'))
            || (this.selectedScrapie === 'No' && !variation.scrapie.includes('No'))
          )
        ) {
          return false;
        }

        return true;
      });
    },

    frequencyOptions() {
      if(this.filteredVariations && this.filteredVariations.length > 0) {
        return Array.from([
          ... new Set(this.filteredVariations.reduce((carry, variation) => {
            variation.frequency && variation.frequency.forEach(f => carry.push(f));

            return carry;
          }, []))
        ])
          .reduce((carry, option) => {
            option = Array.isArray(option) ? option[0] : option;
            carry[option] = option;

            return carry;
          }, {});
      }

      return {'FDX': 'FDX'};
    },

    scrapieOptions() {
      if(this.filteredVariations && this.filteredVariations.length > 0) {
        return Array.from([
          ... new Set(this.filteredVariations.reduce((carry, variation) => {
            variation.scrapie && variation.scrapie.forEach(s => carry.push(s));

            return carry;
          }, []))
        ])
          .sort((a, b) => b.localeCompare(a))
          .reduce((carry, option) => {
            option = Array.isArray(option) ? option[0] : option;
            carry[option] = option;

            return carry;
          }, {});
      }

      return {'No': 'No'};
    },

    hasTemplates() {
      return Object.keys(this.templates).length > 0;
    },

    maleSideImage() {
      if(Object.keys(this.selectedVariation).length === 0) {
        return '';
      }

      return this.$root.imageDomain
              + this.selectedVariation.sku
              + '-M' + (this.selectedScrapie === 'Yes' ? 'S' : '')
              + '.jpg';
    },

    maleVisualMateImage() {
      if(Object.keys(this.selectedVariation).length === 0) {
        return '';
      }

      return this.$root.imageDomain
              + this.selectedVariation.sku
              + '-VM' + (this.selectedScrapie === 'Yes' ? 'S' : '')
              + '.jpg';
    },

    maleTemplates() {
      return Object.keys(this.templates)
        .filter(key => {
          if(
            (this.selectedScrapie === 'Yes' && !this.templates[key].scrapie)
            || (this.selectedScrapie !== 'Yes' && this.templates[key].scrapie)
            || (this.canEid && !this.templates[key].eid)
            || (!this.canEid && this.templates[key].eid)
          ) {
            return false;
          }

          return [BOTH_SIDE, MALE_SIDE].indexOf(this.templates[key].side) > -1
            && [AVAILABLE_MAIN_MATE, AVAILABLE_MAIN].indexOf(this.templates[key].mate_availability) > -1;
        })
        .reduce((carry, key) => {
          carry[key] = this.templates[key];

          return carry;
        }, {});
    },

    vmMaleTemplates() {
      return Object.keys(this.templates)
        .filter(key => [AVAILABLE_MAIN_MATE, AVAILABLE_MATE].indexOf(this.templates[key].mate_availability) > -1)
        .reduce((carry, key) => {
          carry[key] = this.templates[key];

          return carry;
        }, {});
    },

    saveLines() {
      const mapLines = (l) => {
        if(['ain', 'ain_btn', 'ain_multi_line'].indexOf(l.type) !== -1) {
          return 'ain';
        }

        if(l.type === 'ain_last5') {
          return 'ain_last5';
        }

        if(l.type === 'flock') {
          return this.flock.id;
        }

        if(l.type === 'image') {
          return 'logo';
        }

        if(l.type === 'blank') {
          return 'blank';
        }

        return l.value;
      };

      return {
        female: {
          lines: this.filledLines.female.lines.map(mapLines),
          mirror: this.filledLines.female.mirror,
          sequence: this.filledLines.female.sequence,
          template: this.filledLines.female.template,
        },
        ...(this.selectedVariation.panels === 4 ? {
          female_vm: {
            lines: this.filledLines.female_vm.lines.map(mapLines),
            sequence: this.filledLines.female_vm.sequence,
            template: this.filledLines.female_vm.template,
          },
        } : {}),
        male: {
          lines: this.filledLines.male.lines.map(mapLines),
          mirror: this.filledLines.male.mirror,
          sequence: this.filledLines.male.sequence,
          template: this.filledLines.male.template,
        },
        ...(this.selectedVariation.panels === 4 ? {
          male_vm: {
            lines: this.filledLines.male_vm.lines.map(mapLines),
            sequence: this.filledLines.male_vm.sequence,
            template: this.filledLines.male_vm.template,
          },
        } : {}),
      };
    },

    saveProperties() {
      let props = {
        properties: [{
          eartag: {
            eid: this.canEid,
            frequency: this.selectedFrequency,
            lines: this.saveLines,
            scrapie: this.selectedScrapie === 'Yes',
            withLogo: this.saveLines.female.lines.filter(line => line === 'logo').length > 0
              || (this.saveLines.female_vm && this.saveLines.female_vm.lines.filter(line => line === 'logo').length > 0)
              || this.saveLines.male.lines.filter(line => line === 'logo').length > 0
              || (this.saveLines.male_vm && this.saveLines.male_vm.lines.filter(line => line === 'logo').length > 0),
            // note: this.note,
          },
        }],
        qty: [this.quantity],
        sku: [this.selectedVariation.sku],
      };

      if((this.initialData || {}).sku && this.initialData.sku !== this.selectedVariation.sku) {
        props = Object.assign(props, {
          sku: [this.initialData.sku],
          newSku: [this.selectedVariation.sku],
        });
      }

      if(this.uploadedLogo.origname !== '') {
        props.properties[0].eartag.logo = this.uploadedLogo;
      }

      if(this.showValidator || (this.canEid && !this.showValidator)) {
        props.properties[0].eartag.flock = this.flock;
      }

      return props;
    },

    showFemaleTab() {
      return Object.keys(this.femaleTemplates).length > 0;
    },

    showMaleTab() {
      return Object.keys(this.maleTemplates).length > 0;
    },

    showValidator() {
      return this.selectedScrapie === 'Yes' && this.selectedVariation && this.selectedVariation.scrapie.includes('Yes');
    },

    stepDesignCanContinue() {
      return !!this.confirmPreview
        && (!this.showFemaleTab || this.builder1Done)
        && (!this.showFemaleTab || !this.canEid || this.selectedVariation.panels !== 4 || (this.filledLines.female.mirror || this.builder3Done))
        && (!this.showMaleTab || this.builder2Done)
        && (!this.showMaleTab || !this.canEid || this.selectedVariation.panels !== 4 || (this.filledLines.male.mirror || this.builder4Done));
    },

    totalQuantity() {
      return this.quantity * this.packageQty(this.selectedVariation.uom || '');
    },

    toWatchFemaleLines() {
      return Object.assign({}, this.filledLines.female);
    },

    toWatchMaleLines() {
      return Object.assign({}, this.filledLines.male);
    },
  },

  watch: {
    toWatchFemaleLines: {
      deep: true,
      handler: debounce(function(newValue, oldValue) {
        this.updateMateWith(newValue, 'male');

        if(this.selectedVariation.panels !== 4) {
          return;
        }

        if(!newValue.mirror && !oldValue.mirror) {
          return;
        }

        if(newValue.mirror) {
          this.updateVisualMateWith(this.filledLines.female, 'female');
          return;
        }

        const vmTemplate = Object.values(this.vmFemaleTemplates)[0];

        this.filledLines.female_vm = Object.assign(templateMateSide(), {template: vmTemplate.label, lines: vmTemplate.lines});
      }, 350),
    },
    toWatchMaleLines: {
      deep: true,
      handler: debounce(function(newValue, oldValue) {
        if(this.selectedVariation.panels !== 4) {
          return;
        }

        if(!newValue.mirror && !oldValue.mirror) {
          return;
        }

        if(newValue.mirror) {
          this.updateVisualMateWith(this.filledLines.male, 'male');
          return;
        }

        const vmTemplate = Object.values(this.vmMaleTemplates)[0];

        this.filledLines.male_vm = Object.assign(templateMateSide(), {template: vmTemplate.label, lines: vmTemplate.lines});
      }, 350),
    },
  },
}
</script>

<style scoped>
>>> hr {
  background-color: #dbd8d4;
  height: 2px;
}

>>> .invisible {
  opacity: 0;
}

>>> .scrapie-flag {
  background-color: #90cdf4;
  color: #FFF;
  display: inline-block;
  font-size: 0.875em;
  font-weight: bold;
  line-height: 1;
  padding: 0.25em;
  text-transform: uppercase;
}

.step + .step {
  margin-top: 1em;
}

.expand-tab {
  background-color: #F2F3F4;
  border: 0;
  color: #00549d;
  padding: .75em 1.25em;
  width: auto;
  -webkit-appearance: none;
}
.expand-tab:hover {
  color: #000;
}
.expand-tab:focus {
  color: #00549d;
}

.sequence-row {
  background-color: #F2F3F4;
  padding: .5em 1em;
}
.sequence-row:nth-child(even) {
  background-color: #E3E6E8;
}


.expandable-scroll {
  background-color: #F2F3F4;
  max-height: 16em;
  max-width: 24em;
  overflow-y: scroll;
  padding: .35em 0 .35em .5em;
  width: 100%;
}

.expandable-scroll::-webkit-scrollbar {
  -webkit-appearance: none;
  width: .5em;
}
.expandable-scroll::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: rgba(0, 0, 0, .5);
  -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, .5);
  box-shadow: 0 0 1px rgba(255, 255, 255, .5);
}

>>> .relative {
  position: relative;
}

.button-arrowed:after, .button-arrowed-left:before {
  padding: 0;
}

>>> .text-danger {
  color: #b94a48 !important;
}

>>> .wrapping-label.disabled:hover {
  cursor: not-allowed;
  outline: none;
  background: transparent;
  box-shadow: none;
}

>>> .wrapping-label.disabled:hover .label-contents,
.wrapping-label.disabled .label-contents {
  cursor: not-allowed;
  background: transparent;
  outline-color: #BBB;
  box-shadow: none;
}

>>> .wrapping-label input {
  left: 8px;
}

>>> .wrapping-label .fake-check {
  position: absolute;
  top: 50%;
  margin-top: -.4em;
  left: 8px;

  border: 1px solid #4a4a4a;
  display: inline-flex;
  line-height: 1;
  border-radius: 3px;
  width: 1em;
  height: 1em;
  align-items: center;
  justify-items: center;
  font-weight: bold;
  color: #4a4a4a;
  background-color: lightgray;
}

>>> .wrapping-label .fake-check > span {
  display: block;
  font-size: 0.9em;
  text-align: center;
}

/* VALIDATOR */
/deep/ .validator-container {
  background-color: #F2F3F4;
}

@media (min-width: 750px) {
  /deep/ .validator-container {
    max-width: 66.66666%;
  }
}

/deep/ .validator-inner {
  padding: 1.5em;
}

/deep/ .validator-steps {
  align-items: center;
  display: flex;
  line-height: 1;
  margin: 0 -.5em;
}

/deep/ .validator-step {
  align-items: center;
  display: flex;
  margin: 0 0 0 .5em;
  white-space: nowrap;
  overflow: hidden;
  max-width: 2em;
}
/deep/ .validator-step.active-step {
  max-width: 300px;
}

/deep/ .step-num {
  align-items: center;
  border: 2px solid #c1c1c1;
  border-radius: 100%;
  color: #8c8c8c;
  display: flex;
  flex-shrink: 0;
  font-weight: bold;
  height: 2em;
  justify-content: center;
  width: 2em;
}

/deep/ .step-label {
  display: none;
  font-size: 0.875em;
  font-weight: normal;
  line-height: 1.5;
  padding: 0 .5em;
  text-align: right;
  white-space: nowrap;
}

/deep/ .active-step .step-num {
  background-color: #FFF;
  border-color: #00549d;
  color: #00549d;
}
/deep/ .active-step .step-label {
  display: block;
}

/deep/ .past-step .step-num,
/deep/ .completed-step .step-num {
  background-color: #00549d;
  border-color: #00549d;
  color: #FFF;
}

/deep/ .error-text {
  color: #953b39 !important;
  font-size: 0.875em;
  font-weight: 400;
  margin-top: .25em;
}

</style>
